import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import { AccountFacade } from '@mode/accounts/contract';
import { DatasourceFacade } from '@mode/accounts/domain';
import { DatasetFacade } from '@mode/dataset/contract';
import { GoogleSheetSyncFacade } from '@mode/dataset/google/contract';
import {
  DatasetStates,
  DataViewTypes,
  EnhancedDatasetResult,
  ExportedDatasetSourceContent,
  FeatureFlag,
  FeatureFlagsFacade,
  FlamingoTypes,
  ProducerTypes,
} from '@mode/shared/contract-common';
import {
  formatBytes,
  getInitialFlagValue,
  getOwnerUsername,
  isPresent,
  MetaTags,
  MetaTagsService,
} from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { Switch } from '@mode/shared/util-switch';
import { DataViewFacade, SwitchAttrsFacade } from '@mode/switch-views/contract';
import { BehaviorSubject, combineLatest, distinctUntilChanged, Observable, of, Subject } from 'rxjs';
import { combineLatestWith, filter, map, mergeMap, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DatasetOverviewTabDirective, TabState } from '../dataset-overview-tab/dataset-overview-tab.directive';
/* eslint-disable @angular-eslint/component-selector */

export enum TabTypes {
  Data = 'Data',
  Fields = 'Fields',
  Source = 'Source',
}

const defaultTabs: TabState<TabTypes>[] = [
  { title: TabTypes.Data },
  { title: TabTypes.Fields },
  { title: TabTypes.Source },
];

export interface CalcModalEventObj {
  switchViewToken: string;
  viewName: string;
  viewMode?: 'static' | 'edit';
  calc?: FlamingoTypes.ModeCalc;
  readOnly?: boolean;
  duplicate?: boolean;
}

@Component({
  selector: 'mode-dataset-overview',
  templateUrl: './dataset-overview.component.html',
  styleUrls: ['./dataset-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasetOverviewComponent<T = TabTypes> implements OnInit, OnDestroy, AfterContentInit {
  @Input() switchViewToken$!: Observable<string>;
  @Input() activeTab?: TabTypes | T = TabTypes.Data;
  @Input() accountId!: string;
  @Input() reportId!: string;
  @Input() hideCalcFields? = false;
  @Input() hideQueryLink? = false;
  @Input() hideDatasetLink? = false;
  @Input() showFieldDescriptionColumn? = false;
  @Input() showRefreshBtn? = false;
  @Input() showQueryDependenciesBtn = false;
  @Input() showSyncBtn = false;
  @Input() showSyncFailedWarning = false;
  @Input() isRawQuery? = false;
  @Input() fieldDescReadOnly? = false;
  @Input() lastSynced = '';
  @Input() definitionRunState$: Observable<any> = of(null);
  organizationRestricted$: Observable<boolean> = this._accountFacade.hasOrganizationRestricted$;
  @Output() triggerCalcModal = new EventEmitter<CalcModalEventObj>();
  @Output() triggerRemoveCalc = new EventEmitter<CalcModalEventObj>();
  @Output() secondaryHeaderToggle = new EventEmitter<any>();

  @ContentChildren(DatasetOverviewTabDirective) customTabs!: QueryList<DatasetOverviewTabDirective<T>>;
  customDataTab$!: Observable<DatasetOverviewTabDirective<T> | null>;

  tabTypes = TabTypes;
  sourceTypeSwitchView = DataViewTypes.SwitchViewSourceType.SwitchView;
  sourceTypeQuery = DataViewTypes.SwitchViewSourceType.Query;
  fieldFilter!: string;
  fieldDescriptionEditMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  enableNotebookExportDatasets = getInitialFlagValue(FeatureFlag.NotebookExportDatasets);
  enablePublishExportedDataset = getInitialFlagValue(FeatureFlag.NotebookPublishExportedDatasets);
  isRepublishingEnabled = getInitialFlagValue(FeatureFlag.DatasetRepublishing);
  // add feature flag
  tabs: TabState<TabTypes | T>[] = defaultTabs;
  queryToken!: string;
  switchViewToken!: string;

  unsubscribe$: Subject<void>;

  // Copy dataset button
  downloading!: boolean;
  copyTooltipHtml!: string;
  copyButtonHtml!: string;
  largeDatasetForCopy!: string;
  isLargeClipboardEnabled!: boolean;
  isClipboardEnabled!: boolean;
  sizeLimitText!: string;
  copying!: boolean;
  linkToDownload!: string;
  jsonUrl$!: Observable<string>;

  //Footer metadata
  connectionName = '';
  connectionType = '';

  // Common footer data across all tabs
  private dataset$!: Observable<EnhancedDatasetResult>;
  switchView$!: Observable<FlamingoTypes.ModeView>;
  formattedBytesSize$!: Observable<string>;
  csvUrl$!: Observable<string>;
  metaColumns$!: Observable<DataViewTypes.DatasetResourceColumn[]>;
  lastSuccessfulRunAt$!: Observable<string>;
  rowCount$!: Observable<number>;
  executionTime$!: Observable<string>;
  isFallbackData$!: Observable<boolean>;
  exportedDatasetSource$!: Observable<ExportedDatasetSourceContent>;
  isExportedDatasetSuccessful$!: Observable<boolean>;
  isImportedDatasetSuccessful$!: Observable<boolean>;
  isDatasetSuccessful$!: Observable<boolean>;
  //query
  renderedQuery$!: Observable<string>;
  rawQuery$!: Observable<string>;
  switchViewQueryRunState$!: Observable<DataViewTypes.QueryRunStatus>;
  showPersistQueryMsg$!: Observable<boolean>;
  isGoogleSheetSyncing$!: Observable<boolean>;
  isSheetDatasetSuccessful$!: Observable<boolean>;
  isNotebookCellDataset$!: Observable<boolean>;

  isDataRetentionEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.DataRetention);
  isPurged$!: Observable<boolean>;

  // Source
  isSheetDataset$!: Observable<boolean>;
  googleSheetMetadata$!: Observable<DataViewTypes.GoogleSheetResult | null>;
  datasetImportCount$!: Observable<number>;

  //Google Sheet
  googleSheetsEnabled$!: Observable<boolean>;
  readonly googleSyncWarningText = `The latest sync has failed.
  Content <br> using this data will fallback to
  the <br> last successful sync.`;
  readonly purgedWarningText = `The latest run was removed. Reports using this data will break until the Dataset successfully syncs again.`;
  showWarningMsg$!: Observable<boolean>;

  enablePublishCTAForExportedDataset = false;
  enableRepublishCTAForExportedDataset = false;
  constructor(
    private datasetFacade: DatasetFacade,
    private dataViewFacade: DataViewFacade,
    private datasourceFacade: DatasourceFacade,
    private switchAttrsFacade: SwitchAttrsFacade,
    private _accountFacade: AccountFacade,
    private googleSheetSyncFacade: GoogleSheetSyncFacade,
    private _window: Window,
    private analyticsService: AnalyticsService,
    private metaTag: MetaTagsService,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.unsubscribe$ = new Subject();
  }

  ngOnInit(): void {
    this.fieldFilter = '';

    this.dataset$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) =>
          this.dataViewFacade.createEnhancedDatasetResultBySwitchViewToken(
            switchViewToken
          ) as Observable<DataViewTypes.EnhancedDatasetResult>
      )
    );

    this.lastSuccessfulRunAt$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) =>
          this.dataViewFacade.getDatasetResultCompletedAtBySwitchViewToken(switchViewToken) as Observable<string>
      )
    );

    this.exportedDatasetSource$ = this.datasetFacade.exportedDatasetSource$;
    this.formattedBytesSize$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) =>
        this.dataViewFacade
          .getBytesSizeBySwitchViewToken(switchViewToken)
          .pipe(map((bytesSize) => formatBytes(bytesSize as number)))
      )
    );

    this.isPurged$ = this.dataset$.pipe(map((dataset) => dataset?.isPurged));

    this.csvUrl$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) => this.dataViewFacade.getCsvUrlBySwitchViewToken(switchViewToken) as Observable<string>
      )
    );

    this.isFallbackData$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) =>
          this.dataViewFacade.getIsFallbackDataBySwitchViewToken(switchViewToken) as Observable<boolean>
      )
    );

    this.metaColumns$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) =>
          this.dataViewFacade.getMetaColumnsBySwitchViewToken(switchViewToken) as Observable<
            DataViewTypes.DatasetResourceColumn[]
          >
      )
    );

    this.rowCount$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) => this.dataViewFacade.getRowCountBySwitchViewToken(switchViewToken) as Observable<number>
      )
    );

    this.switchView$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) => this.dataViewFacade.createSwitchViewBySwitchViewToken(switchViewToken)),
      combineLatestWith(this.fieldDescriptionEditMode$),
      filter(([, isFieldDescInEditMode]) => {
        return !isFieldDescInEditMode;
      }),
      map(([switchView]) => switchView as FlamingoTypes.ModeView)
    );

    this.renderedQuery$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) => this.dataViewFacade.getRenderedQueryByViewToken(switchViewToken) as Observable<string>
      )
    );

    this.rawQuery$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) => this.dataViewFacade.getRawQueryBySwitchViewToken(switchViewToken) as Observable<string>
      )
    );

    this.jsonUrl$ = this.switchViewToken$.pipe(
      switchMap(
        (switchViewToken) => this.dataViewFacade.getJsonUrlBySwitchViewToken(switchViewToken) as Observable<string>
      )
    );

    // Set query token locally. Currently, we need the query token because all of the downstream rendering for results
    // is still based on queryToken.
    this.switchViewToken$
      .pipe(
        tap((switchViewToken) => (this.switchViewToken = switchViewToken)),
        switchMap((switchViewToken) => this.dataViewFacade.createQueryTokenBySwitchViewToken(switchViewToken)),
        map((queryToken) => {
          // hard coding values of the actual query in the report as there will have to be work related to flamingo
          // signature generation to get imported queries to successfully make flamingo queries.
          this.queryToken = queryToken as string;
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    // Getting the datasource display name and vendor needed for the source tab footer
    // Although the datasources are already loadded on most of the legacy app we need to request it for the new dataset view angular route
    this.datasourceFacade.loadDatasources();
    this.switchViewToken$
      .pipe(
        switchMap((switchViewToken) => this.dataViewFacade.getDatasourceIdBySwitchViewToken(switchViewToken)),
        filter(isPresent),
        switchMap((datasourceId) =>
          this.datasourceFacade.datasources$.pipe(
            mergeMap((datasources) => datasources),
            filter((datasource) => datasource.id === datasourceId)
          )
        ),
        map((datasource) => {
          this.connectionName = datasource.displayName;
          this.connectionType = datasource.vendor;
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.switchViewQueryRunState$ = this.switchViewToken$.pipe(
      filter(isPresent),
      switchMap(
        (switchViewToken) =>
          this.dataViewFacade.getQueryRunStateBySwitchViewToken(
            switchViewToken
          ) as Observable<DataViewTypes.QueryRunStatus>
      ),
      takeUntil(this.unsubscribe$)
    );

    this.isGoogleSheetSyncing$ = combineLatest([
      this.googleSheetSyncFacade.isSyncing$,
      this.switchViewQueryRunState$,
    ]).pipe(
      map(
        ([isGoogleSheetSyncing, queryRunState]) =>
          isGoogleSheetSyncing &&
          ![
            DataViewTypes.QueryRunStatus.Failed,
            DataViewTypes.QueryRunStatus.Cancelled,
            DataViewTypes.QueryRunStatus.NotYetRun,
          ].includes(queryRunState)
      )
    );

    this.showPersistQueryMsg$ = combineLatest([
      this.switchViewQueryRunState$,
      this.definitionRunState$.pipe(map((states) => states?.queryRun?.state)),
    ]).pipe(
      distinctUntilChanged(),
      map(([queryRunState, definitionState]) => {
        return (
          [DataViewTypes.QueryRunStatus.Enqueued].includes(definitionState) ||
          [DataViewTypes.QueryRunStatus.Pending].includes(queryRunState)
        );
      }),
      takeUntil(this.unsubscribe$)
    );

    this.isSheetDataset$ = this.dataset$.pipe(
      map((dataset) => this.datasetFacade.isSheetDataset(dataset?.producerType ?? null))
    );

    this.isNotebookCellDataset$ = this.dataset$.pipe(
      map((dataset) => this.datasetFacade.isNotebookCellDataset(dataset?.producerType))
    );

    if (this.enableNotebookExportDatasets) {
      this.isNotebookCellDataset$.pipe(takeUntil(this.unsubscribe$)).subscribe((isNotebookCellDataset) => {
        if (isNotebookCellDataset) {
          this.datasetFacade.loadExportedDatasetSource(this.queryToken);
        }
      });
    }

    this.datasetImportCount$ = this.datasetFacade.datasetImportCount$;

    this.showWarningMsg$ = this.datasetFacade.datasetImportCount$.pipe(
      combineLatestWith(this.googleSheetSyncFacade.googleSheetSyncError$, this.isSheetDataset$, this.dataset$),
      map(([datasetImportCount, googleSheetSyncError, isSheetDataset, dataset]) => {
        const isImportedByReports = datasetImportCount > 0;
        const syncError = !!googleSheetSyncError;

        if (this.showSyncFailedWarning && isSheetDataset) {
          return dataset.isPurged || ((dataset.isFallbackData || syncError) && isImportedByReports);
        }

        return false;
      })
    );

    this.isSheetDatasetSuccessful$ = this.switchViewQueryRunState$.pipe(
      filter(isPresent),
      combineLatestWith(this.isSheetDataset$),
      filter(([, isSheetDataset]) => !!isSheetDataset),
      map(
        ([queryRunState]) =>
          ![DataViewTypes.QueryRunStatus.Failed, DataViewTypes.QueryRunStatus.Cancelled].includes(queryRunState)
      )
    );

    this.isExportedDatasetSuccessful$ = this.switchViewQueryRunState$.pipe(
      filter(isPresent),
      combineLatestWith(this.isNotebookCellDataset$),
      filter(([, isNotebookCellDataset]) => !!isNotebookCellDataset),
      map(
        ([queryRunState]) =>
          ![DataViewTypes.QueryRunStatus.Failed, DataViewTypes.QueryRunStatus.Cancelled].includes(queryRunState)
      )
    );

    this.isImportedDatasetSuccessful$ = this.switchViewQueryRunState$.pipe(
      filter(isPresent),
      combineLatestWith(this.isNotebookCellDataset$, this.isSheetDataset$),
      filter(([_, isNotebookCellDataset, isSheetDataset]) => !isNotebookCellDataset && !isSheetDataset),
      map(
        ([queryRunState]) =>
          ![DataViewTypes.QueryRunStatus.Failed, DataViewTypes.QueryRunStatus.Cancelled].includes(queryRunState)
      )
    );

    this.isDatasetSuccessful$ = combineLatest([
      this.isImportedDatasetSuccessful$.pipe(startWith(false)),
      this.isSheetDatasetSuccessful$.pipe(startWith(false)),
      this.isExportedDatasetSuccessful$.pipe(startWith(false)),
    ]).pipe(
      map(
        ([isImportedDatasetSuccessful, isSheetDatasetSuccessful, isExportedDatasetSuccessful]) =>
          isImportedDatasetSuccessful || isSheetDatasetSuccessful || isExportedDatasetSuccessful
      )
    );

    this.executionTime$ = combineLatest([
      this.isSheetDataset$,
      this.switchViewToken$.pipe(
        switchMap((switchViewToken) => this.dataViewFacade.getExecutionTimeBySwitchViewToken(switchViewToken))
      ),
    ]).pipe(map(([isSheetDataset, executionTime]) => (isSheetDataset ? '' : executionTime)));

    this.googleSheetMetadata$ = this.dataset$.pipe(map((dataset) => this.datasetFacade.getSheetMetadata(dataset)));

    this.googleSheetsEnabled$ = this._accountFacade.currentOrg$.pipe(
      map((current) => current.googleSheetsEnabled as boolean)
    );

    this.dataset$.pipe(takeUntil(this.unsubscribe$)).subscribe((dataset) => {
      this.enablePublishCTAForExportedDataset = (dataset?.producerType === ProducerTypes.NotebookCell &&
        dataset?.state !== DatasetStates.Orphan &&
        this.enablePublishExportedDataset &&
        !dataset.thoughtspot) as boolean;

      this.enableRepublishCTAForExportedDataset = (dataset?.producerType === ProducerTypes.NotebookCell &&
        dataset?.state !== DatasetStates.Orphan &&
        this.isRepublishingEnabled &&
        !!dataset.thoughtspot) as boolean;
    });
  }

  ngAfterContentInit() {
    this.customTabs.changes
      .pipe(startWith(this.customTabs), takeUntil(this.unsubscribe$))
      .subscribe(this.handleCustomTabsChange.bind(this));
    this.customDataTab$ = this.customTabs.changes.pipe(
      map(
        (customTabs: QueryList<DatasetOverviewTabDirective<T>>): DatasetOverviewTabDirective<T> | null =>
          customTabs.find((t) => t.modeDatasetOverviewTab.title === TabTypes.Data) || null
      )
    );
  }

  ngOnDestroy(): void {
    // Replace this with some general solution in the future.
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  handleCustomTabsChange(customTabs: QueryList<DatasetOverviewTabDirective<T>>): void {
    this.tabs = this.buildTabState(customTabs.map((t) => t.modeDatasetOverviewTab));
  }

  buildTabState(customTabs: TabState<TabTypes | T>[]): TabState<TabTypes | T>[] {
    const clone = (x: any) => x.map((a: any) => ({ ...a }));
    const defaults = clone(defaultTabs);
    const custom = clone(customTabs);
    const updated = [];

    for (const defaultTab of defaults) {
      const update = custom.find((c: any) => c?.title === defaultTab?.title);

      if (update) {
        updated.push(update);
        delete custom[custom.indexOf(update)];
      } else {
        updated.push(defaultTab);
      }
    }

    // combine updated default tabs with new custom tabs
    return [updated, custom].flat();
  }

  /**
   * Helper method used when choosing a new tab. This triggers any tab init if necessary.
   */
  selectTab(targetTab: TabTypes): void {
    this.activeTab = targetTab;
  }

  /**
   * Add new calc.
   *
   */
  addCalculation(viewName: string, sourceType: DataViewTypes.SwitchViewSourceType) {
    const isImportedDataset = this.isImportedDataset(sourceType);
    this.dataViewFacade.openCalcEditor(
      this.switchViewToken,
      viewName,
      'edit',
      undefined,
      false,
      false,
      isImportedDataset,
      this.showFieldDescriptionColumn
    );
  }

  setIsRawQuery(value: boolean) {
    this.isRawQuery = value;
  }

  onEditCalcField(calc: FlamingoTypes.ModeCalc, switchView: FlamingoTypes.ModeView) {
    const isImportedDataset = this.isImportedDataset(switchView.sourceType as DataViewTypes.SwitchViewSourceType);
    this.dataViewFacade.openCalcEditor(
      this.switchViewToken,
      switchView.name,
      'edit',
      calc,
      false,
      false,
      isImportedDataset,
      this.showFieldDescriptionColumn
    );
  }

  onViewCalcField(calc: FlamingoTypes.ModeCalc, switchView: FlamingoTypes.ModeView) {
    const isImportedDataset = this.isImportedDataset(switchView.sourceType as DataViewTypes.SwitchViewSourceType);
    this.dataViewFacade.openCalcEditor(
      this.switchViewToken,
      switchView.name,
      'static',
      calc,
      !calc.isLocal,
      false,
      isImportedDataset,
      this.showFieldDescriptionColumn
    );
  }

  onDuplicateCalcField(calc: FlamingoTypes.ModeCalc, switchView: FlamingoTypes.ModeView) {
    const model = Switch.Models.makeFromView(switchView.name, switchView);
    const newCalc = Switch.Calcs.duplicate(model, switchView.name, calc);

    const isImportedDataset = this.isImportedDataset(switchView.sourceType as DataViewTypes.SwitchViewSourceType);
    this.dataViewFacade.openCalcEditor(
      this.switchViewToken,
      switchView.name,
      'edit',
      newCalc,
      false,
      true,
      isImportedDataset,
      this.showFieldDescriptionColumn
    );
  }

  onRemoveCalcField(calc: FlamingoTypes.ModeCalc, switchView: FlamingoTypes.ModeView) {
    this.dataViewFacade.deleteCalcField(calc, this.switchViewToken, switchView.name);
  }

  toggleHeader($event: any) {
    this.secondaryHeaderToggle.emit({ $event });
  }

  handleFieldFilterChanged(fieldFilter: string) {
    this.fieldFilter = fieldFilter;
  }

  isImportedDataset(sourceType: DataViewTypes.SwitchViewSourceType): boolean {
    return sourceType === this.sourceTypeSwitchView;
  }

  createSwitchAttr(attr: FlamingoTypes.ModeCalc): void {
    this.switchAttrsFacade.createSwitchAttrFromDataView(attr, this.switchViewToken);
  }

  updateSwitchAttr(payload: {
    attr: FlamingoTypes.ModeCalc;
    previousAttr: FlamingoTypes.ModeCalc;
    queryToken?: string;
  }): void {
    this.switchAttrsFacade.updateSwitchAttrFromDataView(
      payload.attr,
      this.switchViewToken,
      payload.previousAttr,
      payload.queryToken as string
    );
  }

  onSyncClick() {
    const url = this._window.location.href;
    const urlArr = url.split('/');
    const datasetId = urlArr[urlArr.length - 1];
    const accountId = getOwnerUsername(this._window.document);

    if (accountId && datasetId) {
      this.googleSheetSyncFacade.datasetEditorSyncGoogleSheet(accountId, datasetId);
    }
  }

  get datasetWebLink(): string {
    return window.location.origin + `/editor/${this.accountId}/reports/${this.reportId}/queries/${this.queryToken}`;
  }

  trackExportClick(event: Event): void {
    event.stopPropagation();

    const eventData = {
      context: this.analyticsService.context,
      location: `${AnalyticsEvent.SubContext.ExportedDataset} Export CTA`,
      action: `${AnalyticsEvent.Name.ExportedDataset}_control-export`,
      name: this.metaTag.getTag(MetaTags.CurrentUsername),
    };
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, eventData);
  }

  publishExportedDataset() {
    this.datasetFacade.openPublishDatasetModal(this.switchViewToken);
    const eventData = {
      context: this.analyticsService.context,
      location: `${AnalyticsEvent.SubContext.DatasetEditor} header CTA`,
      action: AnalyticsEvent.Name.DatasetPublishLaunched,
      name: this.metaTag.getTag(MetaTags.CurrentUsername),
    };
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, eventData);
  }
}
